import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { 
  User, 
  Trophy, 
  Calendar, 
  Star, 
  Play, 
  BookOpen, 
  Award,
  TrendingUp,
  Clock,
  Users,
  LogOut,
  Settings,
  Coins,
  Home,
  Heart,
  Swords,
  UserPlus,
  Edit,
  X,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, token, refreshUser } = useContext(AuthContext);
  const [userProgress, setUserProgress] = useState(null);
  const [cowrieBalance, setCowrieBalance] = useState(user?.cowrie_shells || 0);
  const [loading, setLoading] = useState(true);
  const [showAccountChangeModal, setShowAccountChangeModal] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [changeAccountLoading, setChangeAccountLoading] = useState(false);

  useEffect(() => {
    fetchUserProgress();
    fetchCowrieBalance();
  }, []);

  const fetchUserProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress/orisha_matching`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCowrieBalance = async () => {
    try {
      const response = await axios.get(`${API}/user/cowrie-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCowrieBalance(response.data.cowrie_shells);
    } catch (error) {
      console.error('Error fetching cowrie balance:', error);
      setCowrieBalance(user?.cowrie_shells || 0);
    }
  };

  const games = [
    {
      id: 'orisha_matching',
      title: 'Conocimiento de Orishas',
      description: 'Aprende sobre plantas, animales, colores y días sagrados',
      icon: '🌿',
      difficulty: 'Principiante',
      route: '/games/orisha',
      color: 'from-emerald-500 to-emerald-700',
      available: true
    },
    {
      id: 'ifa_signs',
      title: 'Signos de Ifá',
      description: 'Domina los 256 signos sagrados del sistema Ifá',
      icon: '🔮',
      difficulty: 'Intermedio',
      route: '/games/signs',
      color: 'from-blue-500 to-blue-700',
      available: true
    },
    {
      id: 'sureye_test',
      title: 'Sureye y Patakíes',
      description: '🚧 Proximamente - En desarrollo',
      icon: '📚',
      difficulty: 'Avanzado',
      route: '/games/sureye',
      color: 'from-gray-400 to-gray-500',
      available: false
    },
    {
      id: 'rituals',
      title: 'Rituales Sagrados',
      description: 'Domina ceremonias, moyubas y cantos rituales',
      icon: '🕯️',
      difficulty: 'Experto',
      route: '/games/rituals',
      color: 'from-amber-500 to-amber-700',
      available: true
    },
    {
      id: 'yoruba_language',
      title: 'Idioma Yoruba',
      description: '🚧 Proximamente - En desarrollo',
      icon: '🗣️',
      difficulty: 'Todos los niveles',
      route: '/games/yoruba',
      color: 'from-gray-400 to-gray-500',
      available: false
    }
  ];

  const stats = [
    {
      label: 'Nivel Actual',
      value: user?.level || 1,
      icon: Trophy,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    {
      label: 'Puntos XP',
      value: user?.experience_points || 0,
      icon: Star,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      label: 'Conchas Cowrie',
      value: cowrieBalance,
      icon: Coins,
      color: 'text-amber-600',
      bg: 'bg-amber-100'
    },
    {
      label: 'Juegos Jugados',
      value: userProgress?.total_games_played || 0,
      icon: Play,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    }
  ];

  // New sections for the dashboard
  const newSections = [
    {
      id: 'casa_religiosa',
      title: 'Casa Religiosa',
      description: 'Información sobre tu casa religiosa y linaje espiritual',
      icon: Home,
      color: 'from-red-500 to-red-700',
      route: '/casa-religiosa',
      available: false // Próximamente
    },
    {
      id: 'mis_deidades',
      title: 'Mis Deidades',
      description: 'Tus Orishas tutelares y guías espirituales',
      icon: Heart,
      color: 'from-pink-500 to-pink-700',
      route: '/mis-deidades',
      available: false // Próximamente
    },
    {
      id: 'duelos',
      title: 'Duelos',
      description: 'Desafía a otros practicantes en conocimiento sagrado',
      icon: Swords,
      color: 'from-orange-500 to-orange-700',
      route: '/duelos',
      available: false // Próximamente
    },
    {
      id: 'amigos',
      title: 'Amigos',
      description: 'Conecta con otros estudiantes del camino sagrado',
      icon: UserPlus,
      color: 'from-indigo-500 to-indigo-700',
      route: '/amigos',
      available: false // Próximamente
    }
  ];

  const getAccountTypeLabel = (role) => {
    switch(role) {
      case 'student': return 'Aleyo';
      case 'santero': return 'Santero';
      case 'babalawo': return 'Babalawo';
      case 'admin': return 'Administrador';
      default: return 'Aleyo';
    }
  };

  const handleAccountChangeRequest = async () => {
    if (!selectedAccountType) return;
    
    setChangeAccountLoading(true);
    try {
      const response = await axios.post(`${API}/user/account-change-request`, {
        requested_role: selectedAccountType,
        current_role: user?.role || 'student'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('¡Solicitud enviada exitosamente! Tu solicitud será revisada por un administrador.');
      setShowAccountChangeModal(false);
      setSelectedAccountType('');
    } catch (error) {
      console.error('Error submitting account change request:', error);
      alert('Error al enviar la solicitud. Inténtalo de nuevo más tarde.');
    } finally {
      setChangeAccountLoading(false);
    }
  };

  const achievements = [
    {
      title: 'Primer Paso',
      description: 'Completa tu primer juego',
      icon: '🎯',
      unlocked: (userProgress?.total_games_played || 0) > 0
    },
    {
      title: 'Estudiante Dedicado',
      description: 'Juega 5 partidas',
      icon: '📚',
      unlocked: (userProgress?.total_games_played || 0) >= 5
    },
    {
      title: 'Maestro de Orishas',
      description: 'Obtén 90% o más en el juego de Orishas',
      icon: '👑',
      unlocked: (userProgress?.best_score || 0) >= 90
    },
    {
      title: 'Perfeccionista',
      description: 'Obtén una puntuación perfecta',
      icon: '⭐',
      unlocked: (userProgress?.best_score || 0) === 100
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-yellow-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium text-lg">Cargando tu progreso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-2 border-emerald-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_955bee34-da15-4f79-944e-efcba3acaf46/artifacts/6o2v55w8_CaminosdeIfa_Linkedin.jpg"
                alt="Caminos de Ifá"
                className="w-12 h-12 rounded-full border-2 border-emerald-500"
              />
              <div>
                <h1 className="font-cinzel text-2xl font-bold text-emerald-800">Dashboard</h1>
                <p className="text-emerald-600">¡Bienvenido, {user?.full_name}!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="level-badge">
                <Trophy className="w-4 h-4" />
                Nivel {user?.level}
              </div>
              <Link 
                to="/profile"
                className="p-2 text-emerald-600 hover:text-emerald-800 transition-colors"
              >
                <Settings className="w-6 h-6" />
              </Link>
              <button 
                onClick={logout}
                className="p-2 text-red-600 hover:text-red-800 transition-colors"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Account Change Modal */}
      {showAccountChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800">Cambiar Tipo de Cuenta</h2>
              <button 
                onClick={() => setShowAccountChangeModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Tu tipo de cuenta actual es: <span className="font-semibold text-emerald-600">{getAccountTypeLabel(user?.role)}</span>
              </p>
              <p className="text-gray-600 text-sm mb-6">
                Selecciona el nuevo tipo de cuenta al que deseas cambiar. Tu solicitud será revisada por un administrador.
              </p>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-emerald-300 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="accountType"
                    value="santero"
                    checked={selectedAccountType === 'santero'}
                    onChange={(e) => setSelectedAccountType(e.target.value)}
                    className="text-emerald-600"
                  />
                  <div>
                    <span className="font-semibold text-gray-800">Santero</span>
                    <p className="text-sm text-gray-600">Practicante iniciado en la religión Yoruba</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-emerald-300 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="accountType"
                    value="babalawo"
                    checked={selectedAccountType === 'babalawo'}
                    onChange={(e) => setSelectedAccountType(e.target.value)}
                    className="text-emerald-600"
                  />
                  <div>
                    <span className="font-semibold text-gray-800">Babalawo</span>
                    <p className="text-sm text-gray-600">Sacerdote de Ifá con conocimiento avanzado</p>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => setShowAccountChangeModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAccountChangeRequest}
                disabled={!selectedAccountType || changeAccountLoading}
                className="flex-1 bg-emerald-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {changeAccountLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Enviar Solicitud
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mb-4`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-emerald-800">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Games Section */}
          <div className="lg:col-span-2 space-y-8">
            {/* Games Section */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <Play className="w-6 h-6 text-emerald-600" />
                <h2 className="font-cinzel text-2xl font-bold text-emerald-800">Juegos Disponibles</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {games.map((game, index) => (
                  <div key={index} className={`game-card bg-gradient-to-br ${game.color} p-6 rounded-2xl text-white relative overflow-hidden ${!game.available ? 'opacity-75' : ''}`}>
                    {!game.available && (
                      <div className="absolute top-4 right-4 bg-white text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">
                        Próximamente
                      </div>
                    )}
                    
                    <div className="text-4xl mb-4">{game.icon}</div>
                    <h3 className="font-cinzel text-xl font-bold mb-2">{game.title}</h3>
                    <p className="text-white text-opacity-90 text-sm mb-4 leading-relaxed">
                      {game.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-white bg-opacity-20 px-3 py-1 rounded-full">
                        {game.difficulty}
                      </span>
                      
                      {game.available ? (
                        <Link
                          to={game.route}
                          className="bg-white text-emerald-800 px-4 py-2 rounded-xl font-semibold text-sm hover:scale-105 transition-all duration-300 flex items-center gap-2 z-10 relative"
                        >
                          <Play className="w-4 h-4" />
                          Jugar
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="bg-white bg-opacity-30 text-white px-4 py-2 rounded-xl font-semibold text-sm cursor-not-allowed"
                        >
                          Bloqueado
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* New Sections */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-emerald-600" />
                <h2 className="font-cinzel text-2xl font-bold text-emerald-800">Comunidad y Práctica</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {newSections.map((section, index) => (
                  <div key={index} className={`game-card bg-gradient-to-br ${section.color} p-6 rounded-2xl text-white relative overflow-hidden ${!section.available ? 'opacity-75' : ''}`}>
                    {!section.available && (
                      <div className="absolute top-4 right-4 bg-white text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">
                        Próximamente
                      </div>
                    )}
                    
                    <section.icon className="w-10 h-10 mb-4" />
                    <h3 className="font-cinzel text-xl font-bold mb-2">{section.title}</h3>
                    <p className="text-white text-opacity-90 text-sm mb-4 leading-relaxed">
                      {section.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-white bg-opacity-20 px-3 py-1 rounded-full">
                        Funcionalidad Social
                      </span>
                      
                      {section.available ? (
                        <Link
                          to={section.route}
                          className="bg-white text-emerald-800 px-4 py-2 rounded-xl font-semibold text-sm hover:scale-105 transition-all duration-300 flex items-center gap-2 z-10 relative"
                        >
                          <Play className="w-4 h-4" />
                          Ver
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="bg-white bg-opacity-30 text-white px-4 py-2 rounded-xl font-semibold text-sm cursor-not-allowed"
                        >
                          Próximamente
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <h3 className="font-cinzel text-lg font-bold text-emerald-800">Tu Progreso</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Experiencia</span>
                    <span className="text-emerald-600 font-semibold">{user?.experience_points || 0} XP</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${Math.min(((user?.experience_points || 0) % 1000) / 10, 100)}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="text-center py-4">
                  <div className="text-3xl mb-2">🎯</div>
                  <p className="text-sm text-gray-600">
                    {userProgress?.total_games_played > 0 
                      ? `¡Sigue así! Llevas ${userProgress.total_games_played} juegos completados`
                      : 'Comienza tu primer juego para ver tu progreso'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Achievements Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-emerald-600" />
                <h3 className="font-cinzel text-lg font-bold text-emerald-800">Logros</h3>
              </div>
              
              <div className="space-y-3">
                {achievements.map((achievement, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-xl border-2 transition-all duration-300 ${
                      achievement.unlocked 
                        ? 'border-emerald-200 bg-emerald-50' 
                        : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{achievement.icon}</div>
                      <div className="flex-1">
                        <h4 className={`font-semibold text-sm ${
                          achievement.unlocked ? 'text-emerald-800' : 'text-gray-600'
                        }`}>
                          {achievement.title}
                        </h4>
                        <p className={`text-xs ${
                          achievement.unlocked ? 'text-emerald-600' : 'text-gray-500'
                        }`}>
                          {achievement.description}
                        </p>
                      </div>
                      {achievement.unlocked && (
                        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Star className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-primary rounded-2xl p-6 text-white shadow-lg">
              <h3 className="font-cinzel text-lg font-bold mb-4">Estadísticas Rápidas</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="opacity-90">Tipo de cuenta</span>
                  <span className="font-semibold">{getAccountTypeLabel(user?.role)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-90">Dificultad actual</span>
                  <span className="font-semibold">{user?.current_difficulty || 'Principiante'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-90">Idioma</span>
                  <span className="font-semibold">
                    {user?.language === 'es' ? '🇪🇸 Español' : 
                     user?.language === 'en' ? '🇺🇸 English' : 
                     '🇫🇷 Français'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="opacity-90">Miembro desde</span>
                  <span className="font-semibold">
                    {new Date(user?.created_at).toLocaleDateString('es-ES', { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                
                {/* Admin Panel Button */}
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="w-full mt-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Panel de Administración
                  </Link>
                )}
                
                {/* Account Change Button */}
                {user?.role !== 'admin' && (
                  <button
                    onClick={() => setShowAccountChangeModal(true)}
                    className="w-full mt-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Cambiar Tipo de Cuenta
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;